import { HelpFactoryComponent } from '@/components/common';
import { HelpFeature } from '@/model/HelpModel';
import { Button, Checkbox, Input, Spin } from 'antd';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';
import cx from 'classnames';
import React, { useEffect, useState } from 'react';
import { batch } from 'react-redux';
import { handleResponse } from '../../../../../Helper';
import { deviceAdminApi } from '../../../../../api';
import { SecuredComponents, User, getPermissionErrorMessage } from '../../../../../model/AccountModel';
import { RoleResponseObject } from '../../../../../model/DeviceAdminModel';
import { Modal } from '../../../../common/Modal/Modal';
import styles from './addRolesModal.module.scss';

type Props = {
	isCopyMode: boolean;
	copyFromRoleId?: number;
	onConfirm: () => void;
	onCancel: () => void;
};

const { TextArea } = Input;

//Avoid creating object style inline, since increases reconciliations
const user: User = getUser();
const roleNameInputId = 'roleNameInput';
const roleDescriptionInputId = 'roleDescriptionInput';
const topicIdToAddNewRole: string = 'ToAddNewRole';
const topicIdToCopyRole: string = 'ToCopyRole';

const AddRolesModal: React.FC<Props> = ({ isCopyMode, copyFromRoleId, onConfirm, onCancel }) => {
	//#region state management variables
	const [name, setName] = useState<string>('');
	const [description, setDescription] = useState<string>('');
	const [createWithAllPermissions, setCreateWithAllPermissions] = useState<boolean>(false);
	const [viewEmptyNameError, setViewEmptyNameError] = useState<boolean>(false);
	const [emptyNameError, setEmptyNameError] = useState<string>('');
	const [copyName, setCopyName] = useState<string>();
	const [isLoading, setIsLoading] = useState<boolean>(false);
	//#endregion

	useEffect(() => {
		deviceAdminApi.getErrorMessages(SecuredComponents.Roles).then(response => setEmptyNameError(response.EmptyName));
		if (isCopyMode) {
			deviceAdminApi.getRoleBasicInformation(copyFromRoleId).then(response => {
				if (!handleResponse(response)) {
					const { Description, RoleName } = response.Entity;
					batch(() => {
						setName(`${RoleName} (Copy)`);
						setDescription(Description);
						setCopyName(RoleName);
					});
				}
			});
		}
	}, []);

	const handleOnClickSave = async (): Promise<void> => {
		if (!name?.trim()) {
			setViewEmptyNameError(true);
			return;
		}

		setIsLoading(true);
		let response: RoleResponseObject;
		if (isCopyMode) {
			response = await deviceAdminApi.copyRole(copyFromRoleId, name, description);
		} else {
			response = await deviceAdminApi.createNewRole(name, description, createWithAllPermissions);
		}
		setIsLoading(false);
		if (!handleResponse(response)) {
			onConfirm();
		}
	};

	return (
		<Modal
			footer={[
				<Button
					id='roleSaveButton'
					title={getPermissionErrorMessage(user.isAdmin)}
					key='save'
					type='primary'
					disabled={!user.isAdmin || isLoading}
					onClick={handleOnClickSave}>
					{_('SaveChanges')}
				</Button>,
				<Button disabled={isLoading} id='roleCancelButton' key='cancel' onClick={onCancel}>
					{_('Cancel')}
				</Button>,
			]}
			visible
			title={isCopyMode ? `${_('CopyRole')} (${copyName})` : _('AddNewRole')}
			onCancel={onCancel}
			customZoomClass={styles.WithModalZoom}
			addHelpButton={<HelpFactoryComponent helpFeature={HelpFeature.Roles} topicId={isCopyMode ? topicIdToCopyRole : topicIdToAddNewRole} />}>
			<Spin size='large' tip={`${_('Loading')}...`} spinning={isLoading}>
				<div className={styles.sections}>
					<label id='roleNameLabel' htmlFor={roleNameInputId}>
						{_('Name')}
					</label>
					<Input
						id={roleNameInputId}
						key='name'
						value={name}
						onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
							setName(e.target.value);
						}}
						maxLength={32}
						className={cx(styles.input, {
							[styles.error]: viewEmptyNameError,
						})}
					/>
				</div>
				{viewEmptyNameError && <p className={styles.errorMessage}>{emptyNameError}</p>}
				<div className={styles.sections}>
					<label id='roleDescriptionLabel' htmlFor={roleDescriptionInputId}>
						{_('Description')}
					</label>
					<TextArea
						id={roleDescriptionInputId}
						className={styles.inputWithBottom}
						value={description}
						onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
							setDescription(e.target.value);
						}}
						maxLength={125}
						rows={4}
					/>
				</div>
				{!isCopyMode && (
					<div className={styles.containerCheckbox}>
						<Checkbox
							id='addRoleCreateWithPermissionsTurnedOnCheckbox'
							checked={createWithAllPermissions}
							onChange={(e: CheckboxChangeEvent) => {
								setCreateWithAllPermissions(e.target.checked);
							}}>
							<label htmlFor='addRoleCreateWithPermissionsTurnedOnCheckbox'> {_('CreateWithPermissionsTurnedOn')}</label>
						</Checkbox>
					</div>
				)}
			</Spin>
		</Modal>
	);
};

export { AddRolesModal };
